import { SOUND_PIN } from "../systems/sound-effects-system";
import { onThemeChanged } from "../utils/theme";
import { guessContentType } from "../utils/media-url-utils";
import configs from "../utils/configs";
import { getNetworkedEntity } from "../utils/scene-graph";

AFRAME.registerComponent("pin-networked-object-button", {
  schema: {
    // Selector for label to change when pinned/unpinned, must be sibling of this components element
    labelSelector: { type: "string" },

    // Selector for icon to change when pinned/unpinned, must be sibling of this components element
    iconSelector: { type: "string" }
  },

  init() {
    this._updateUI = this._updateUI.bind(this);
    this._updateUIOnStateChange = this._updateUIOnStateChange.bind(this);
    this.el.sceneEl.addEventListener("stateadded", this._updateUIOnStateChange);
    this.el.sceneEl.addEventListener("stateremoved", this._updateUIOnStateChange);

    this.labelEl = this.el.parentNode.querySelector(this.data.labelSelector);
    this.iconEl = this.el.parentNode.querySelector(this.data.iconSelector);

    getNetworkedEntity(this.el)
      .then(networkedEl => {
        this.targetEl = networkedEl;

        this._updateUI();
        this.targetEl.addEventListener("pinned", this._updateUI);
        this.targetEl.addEventListener("unpinned", this._updateUI);
        this.targetEl.addEventListener("locked", this._updateUI);
        this.targetEl.addEventListener("unlocked", this._updateUI);
        this.targetEl.addEventListener("object-selected", this._updateUI);
      })
      .catch(err => {
        console.error("Error getting networked entity for: ", this.el, err);
      });

    this.onHovered = () => {
      this.hovering = true;
      this._updateUI();
    };

    this.onUnhovered = () => {
      this.hovering = false;
      this._updateUI();
    };

    this.onClick = () => {
      if (!NAF.utils.isMine(this.targetEl) && !NAF.utils.takeOwnership(this.targetEl)) return;

      const wasPinned =
        (this.targetEl.components.pinnable && this.targetEl.components.pinnable.data.pinned) ||
        (this.targetEl.components.lockeable && this.targetEl.components.lockeable.data.locked);

      if (this.targetEl.components.lockeable) {
        window.APP.lockHelper.setLocked(this.targetEl, !wasPinned);
      } else {
        window.APP.pinningHelper.setPinned(this.targetEl, !wasPinned);
      }
      if (!wasPinned) {
        this.el.sceneEl.systems["hubs-systems"].soundEffectsSystem.playSoundOneShot(SOUND_PIN);
      }
    };

    this.removeThemeChangedListener = onThemeChanged(this._updateUI);
  },

  play() {
    this.el.object3D.addEventListener("interact", this.onClick);
    this.el.object3D.addEventListener("hovered", this.onHovered);
    this.el.object3D.addEventListener("unhovered", this.onUnhovered);
    this.el.sceneEl.addEventListener("object-selected", this._updateUI);
  },

  pause() {
    this.el.object3D.removeEventListener("interact", this.onClick);
    this.el.object3D.removeEventListener("hovered", this.onHovered);
    this.el.object3D.removeEventListener("unhovered", this.onUnhovered);
    this.el.sceneEl.removeEventListener("object-selected", this._updateUI);
  },

  remove() {
    this.el.sceneEl.removeEventListener("stateadded", this._updateUIOnStateChange);
    this.el.sceneEl.removeEventListener("stateremoved", this._updateUIOnStateChange);
    this.el.sceneEl.removeEventListener("object-selected", this._updateUI);

    if (this.targetEl) {
      this.targetEl.removeEventListener("pinned", this._updateUI);
      this.targetEl.removeEventListener("unpinned", this._updateUI);
      this.targetEl.removeEventListener("locked", this._updateUI);
      this.targetEl.removeEventListener("unlocked", this._updateUI);
    }

    this.removeThemeChangedListener();
  },

  _discordBridges() {
    return window.APP.hubChannel.discordBridges();
  },

  _updateUIOnStateChange(e) {
    if (e.detail !== "frozen") return;
    this._updateUI();
  },

  async _updateUI() {
    const mediaLoader = this.targetEl.components["media-loader"];
    const url =
      mediaLoader && ((mediaLoader.data.mediaOptions && mediaLoader.data.mediaOptions.href) || mediaLoader.data.src);

    const contentType = guessContentType(url);
    const canLockObjects =
      contentType === "video/vnd.hubs-webrtc"
        ? this.targetEl.hasAttribute("emit-scene-event-on-remove") || configs.isAdmin()
        : true;

    const canPin = window.APP.hubChannel.can("pin_objects");

    if (!canPin || !canLockObjects) {
      this.el.object3D.visible = false;
      this.labelEl.object3D.visible = false;

      return;
    }

    this.el.object3D.visible = canPin;
    this.labelEl.object3D.visible = canPin;

    const isPinned = this.targetEl.getAttribute("pinnable") && this.targetEl.getAttribute("pinnable").pinned;
    const isLocked = this.targetEl.getAttribute("lockeable") && this.targetEl.getAttribute("lockeable").locked;

    this.labelEl.setAttribute("text", "value", isPinned || isLocked ? "Unlock" : "Lock");
    this.iconEl.setAttribute("icon-button", "active", isPinned || isLocked);
    // applyThemeToTextButton(this.el, isPinned || isLocked);
  }
});
